import styled, { AnyStyledComponent } from 'styled-components';
import { FC } from 'react';

// Construct the styled components wrapper for the img element
interface ImageWrapperProps {
  rounded: boolean;
  w: string | number;
  h: string | number;
  border?: string;
  visible: boolean;
  borderRadius?: string;
};

export const ImageWrapper: AnyStyledComponent = styled.div`
  display: inline-block;
  width: ${({w}: ImageWrapperProps) => w };
  height: ${({h}: ImageWrapperProps) => h};
  border-radius: ${({rounded, borderRadius}: ImageWrapperProps) => rounded ? '50%': (borderRadius ? borderRadius : '')};
  -webkit-border-radius: ${({rounded, borderRadius}: ImageWrapperProps) => rounded ? '50%': (borderRadius ? borderRadius : '')};
  overflow: ${({rounded, borderRadius}: ImageWrapperProps) => (rounded || borderRadius) ? 'hidden' : '' };
  border: ${({rounded, border}: ImageWrapperProps) => (rounded && border) ? border : ''};
  position: relative;
  ${({visible}: ImageWrapperProps) => !visible && 'visibility: hidden;' }
`;


interface ImgProps {
  padding?: string;
  backgroundColor?: string;
  rounded?: boolean;
  borderRadius?: string;
};

export const Img: AnyStyledComponent = styled.img`
  box-sizing: border-box;
  padding: ${({ padding }: ImgProps) => padding};
  background-color: ${({ backgroundColor }: ImgProps) => backgroundColor };
  &:-moz-loading {
    color: ${({ backgroundColor }: ImgProps) => backgroundColor };
  }
  border-radius: ${({rounded, borderRadius}: ImgProps) => rounded ? '50%': (borderRadius ? borderRadius : '')};
  -webkit-border-radius: ${({rounded, borderRadius}: ImgProps) => rounded ? '50%': (borderRadius ? borderRadius : '')};
`

// Construct the styled placeholder using several variables + backups
export const styledPlaceholder: Function = (
  height: number | string | undefined,
  width: number | string,
  placeholderWidth: number | string | undefined,
  placeholderHeight: number | string | undefined,
  backgroundColor: string | undefined,
  ratio: string = '1:1',
  border: string | undefined): FC => {

  let h: number | string | undefined = placeholderHeight || height;
  if (h === undefined) {
    h = retrieveHeightFromRatio(width, ratio);
  }

  return styled.div`
      height: ${ h };
      width: ${ placeholderWidth || width};
      ${ backgroundColor && `background-color: ${backgroundColor};` }
      border-radius: 8px;
      ${ border && `border: ${border};` }
  `;
};

// Construct a height value from the width and ratio passed
const retrieveHeightFromRatio: Function = (
  placeholderHeight: number | string | undefined,
  height: string | number | undefined,
  width: number | string,
  ratio: string = '1:1'): number | string => {

  const h: number | string | undefined = placeholderHeight || height;
  if (typeof h !== 'undefined') {
    return h;
  }

  const ratioArr: string[] = ratio.split(':');
  const rWidth: number = parseFloat(ratioArr[0]);
  const rHeight: number = parseFloat(ratioArr[1]);

  if (typeof width === 'string') {
    // Since we're probably dealing with 'px' or '%' in the string, this separates
    // the number from the unit and stores both for use later on.
    const parsedWidth: string[] = width.replace(/\'/g, '').split(/(\d+)/);
    const numValue: number = parseFloat(parsedWidth[1]);
    const unit: string = parsedWidth[2];
    return `${ numValue * rHeight / rWidth }${ unit || 'px' }`;
  } else {
    return (width * rHeight) / rWidth;
  }
};

interface PlaceholderProps {
  w: string | number;
  h?: string | number;
  placeholderWidth?: number | string;
  placeholderHeight?: number | string;
  backgroundColor?: string;
  border?: string;
  ratio: string;
};

export const Placeholder: AnyStyledComponent = styled.div`
  height: ${({placeholderHeight, h, w, ratio}: PlaceholderProps) =>
    retrieveHeightFromRatio(placeholderHeight, h, w, ratio) };
  width: ${({placeholderWidth, w}: PlaceholderProps) => placeholderWidth || w};
  background-color: ${({backgroundColor}: PlaceholderProps) =>backgroundColor};
  border-radius: 8px;
  border: ${({border}: PlaceholderProps) => border};
`;
